import { doc, updateDoc, getDoc, addDoc, collection, serverTimestamp, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

// E-SIGN Act compliant e-signature service
export class ESignatureService {
  // Get client information for audit trail
  static async getClientInfo() {
    try {
      // Get IP address (this would need to be implemented server-side in production)
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      
      return {
        ipAddress: ipData.ip,
        userAgent: navigator.userAgent,
        browser: this.getBrowserInfo(),
        device: this.getDeviceInfo(),
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    } catch (error) {
      console.error('Error getting client info:', error);
      return {
        ipAddress: 'Unknown',
        userAgent: navigator.userAgent,
        browser: this.getBrowserInfo(),
        device: this.getDeviceInfo(),
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    }
  }

  static getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    else if (userAgent.includes('Opera')) browser = 'Opera';
    
    return browser;
  }

  static getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let device = 'Desktop';
    
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      device = 'Mobile';
    } else if (/iPad|Android.*Tablet/i.test(userAgent)) {
      device = 'Tablet';
    }
    
    return device;
  }

  // Create a tamper-proof snapshot of the document content
  static createDocumentSnapshot(documentContent, formData) {
    const snapshot = {
      documentType: 'Non-Disclosure Agreement',
      documentContent: documentContent,
      formData: formData,
      snapshotTimestamp: new Date().toISOString(),
      hash: this.generateHash(documentContent + JSON.stringify(formData))
    };
    
    return snapshot;
  }

  // Simple hash function for document integrity (in production, use a more robust hashing algorithm)
  static generateHash(content) {
    let hash = 0;
    if (content.length === 0) return hash.toString();
    
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString();
  }

  /**
   * True when this flow should be treated as a service/program contract (not an NDA).
   * NDAs often contain "Agreement" (e.g. Non-Disclosure Agreement) — that must not imply contract.
   */
  static isContractSigning(formData, documentData) {
    const doc = typeof documentData === 'string' ? documentData : '';
    if (formData.documentType === 'nda' || formData.isNDA === true) {
      return false;
    }
    if (formData.isContract === true || formData.documentType === 'contract') {
      return true;
    }
    if (/non-disclosure|confidentiality\s+agreement|mutual\s+nda|\bnda\b/i.test(doc)) {
      return false;
    }
    return !!(
      formData.contractType ||
      formData.programName ||
      formData.program?.name ||
      doc.includes('Service Agreement') ||
      doc.includes('Contract') ||
      doc.includes('Agreement') ||
      doc.includes('Program') ||
      doc.includes('Terms of Service')
    );
  }

  // Save e-signature with full audit trail
  static async saveESignature(userId, documentData, formData, consentData) {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to sign documents');
      }

      console.log('ESignatureService.saveESignature - formData received:', formData);
      console.log('ESignatureService.saveESignature - formData.initial:', formData.initial);

      const clientInfo = await this.getClientInfo();
      const documentSnapshot = this.createDocumentSnapshot(documentData, formData);
      
      console.log('🔍 Document type detection - formData:', formData);
      console.log('🔍 Document type detection - documentData preview:', documentData?.substring(0, 200));
      
      const isContract = this.isContractSigning(formData, documentData);
      console.log('🔍 Contract detection result:', isContract);
      
      // Create the signed document record
      const signedDocument = {
        id: isContract ? `contract-${Date.now()}` : `nda-${Date.now()}`,
        title: isContract ? 
          `Signed ${formData.contractType || formData.programName || 'Service Agreement'}` : 
          "Signed Non-Disclosure Agreement",
        description: isContract ? 
          `${formData.contractType || formData.programName || 'Contract'} signed by ${formData.signerName} (${formData.signerTitle}) on behalf of ${formData.companyName}` :
          `NDA signed by ${formData.signerName} (${formData.signerTitle}) on behalf of ${formData.companyName}`,
        foundIn: "Legal Documents",
        pillar: "General",
        category: "Contracts",
        type: "PDF",
        unlocked: true,
        signedAt: new Date().toISOString(),
        signerName: formData.signerName,
        signerTitle: formData.signerTitle,
        signerEmail: auth.currentUser.email,
        companyName: formData.companyName,
        companyAddress: formData.companyAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        businessStructure: formData.businessStructure,
        signature: formData.signature,
        initial: formData.initial || '',
        isSignedNDA: !isContract,
        isSignedContract: isContract,
        
        // E-SIGN Act Compliance Data
        eSignCompliance: {
          intentToSign: true,
          consentToElectronicSignatures: consentData.consentToElectronic,
          consentToElectronicRecords: consentData.consentToRecords,
          signatureAttribution: {
            fullName: formData.signerName,
            email: auth.currentUser.email,
            ipAddress: clientInfo.ipAddress,
            timestamp: clientInfo.timestamp
          },
          recordIntegrity: {
            documentSnapshot: documentSnapshot,
            tamperProofHash: documentSnapshot.hash
          },
          auditTrail: {
            ipAddress: clientInfo.ipAddress,
            userAgent: clientInfo.userAgent,
            browser: clientInfo.browser,
            device: clientInfo.device,
            timezone: clientInfo.timezone,
            signedDateTime: clientInfo.timestamp,
            consentTimestamp: consentData.consentTimestamp
          }
        }
      };

      console.log('ESignatureService.saveESignature - final signedDocument:', signedDocument);
      console.log('ESignatureService.saveESignature - signedDocument.initial:', signedDocument.initial);

      // Save to user's collection
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const existingCollection = userData.collection || [];
        const updatedCollection = [...existingCollection, signedDocument];
        
        await updateDoc(userDocRef, {
          collection: updatedCollection,
          'onboardingStatus.steps.nda.completed': true
        });
      }

      // Create audit trail record
      await this.createAuditTrailRecord(userId, signedDocument, clientInfo);

      // Determine document type and trigger appropriate notification
      try {
        const isContractNotify = this.isContractSigning(formData, documentData);
        
        if (isContractNotify) {
          // Trigger contract signed notification
          const { triggerContractSignedNotification } = await import('./notificationTriggerService');
          await triggerContractSignedNotification({
            clientName: formData.signerName,
            company: formData.companyName,
            contractType: formData.contractType || formData.programName || 'Service Agreement',
            signedDate: new Date().toISOString(),
            documentId: signedDocument.id,
            status: 'signed',
            signerTitle: formData.signerTitle,
            signerEmail: auth.currentUser.email,
            timestamp: new Date().toISOString()
          });
          console.log('✅ Contract signed notification sent successfully');
        } else {
          // Trigger NDA signed notification
          const { triggerNDASignedNotification } = await import('./notificationTriggerService');
          await triggerNDASignedNotification({
            clientName: formData.signerName,
            company: formData.companyName,
            signedDate: new Date().toISOString(),
            documentId: signedDocument.id,
            status: 'signed',
            documentType: 'NDA',
            signerTitle: formData.signerTitle,
            signerEmail: auth.currentUser.email,
            timestamp: new Date().toISOString()
          });
          console.log('✅ NDA signed notification sent successfully');
        }
      } catch (notificationError) {
        console.error('❌ Error sending document signed notification:', notificationError);
        // Don't fail the signing if notification fails
      }

      return signedDocument;
    } catch (error) {
      console.error('Error saving e-signature:', error);
      throw error;
    }
  }

  // Create separate audit trail record
  static async createAuditTrailRecord(userId, signedDocument, clientInfo) {
    try {
      const auditTrailRef = collection(db, 'auditTrails');
      
      const auditRecord = {
        userId: userId,
        documentId: signedDocument.id,
        documentType: 'Non-Disclosure Agreement',
        action: 'document_signed',
        timestamp: serverTimestamp(),
        clientInfo: clientInfo,
        signatureData: {
          signerName: signedDocument.signerName,
          signerEmail: signedDocument.signerEmail,
          companyName: signedDocument.companyName,
          signature: signedDocument.signature
        },
        eSignCompliance: signedDocument.eSignCompliance
      };

      await addDoc(auditTrailRef, auditRecord);
    } catch (error) {
      console.error('Error creating audit trail:', error);
      // Don't throw error here as the main signature should still be saved
    }
  }

  // Validate E-SIGN Act compliance
  static validateESignCompliance(consentData, formData) {
    const errors = [];

    if (!consentData.consentToElectronic) {
      errors.push('Consent to electronic signatures is required');
    }

    if (!consentData.consentToRecords) {
      errors.push('Consent to electronic records is required');
    }

    if (!formData.signerName || !formData.signerName.trim()) {
      errors.push('Signer full name is required');
    }

    if (!formData.signature || !formData.signature.trim()) {
      errors.push('Electronic signature is required');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // Get audit trail for a document
  static async getAuditTrail(documentId) {
    try {
      const auditTrailRef = collection(db, 'auditTrails');
      const q = query(auditTrailRef, where('documentId', '==', documentId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting audit trail:', error);
      throw error;
    }
  }

  // Save BBA signature to complete NDA execution
  static async saveBBASignature(userId, documentId, bbaFormData, clientInfo) {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to sign documents');
      }

      // Create BBA signature record
      const bbaSignature = {
        bbaSignerName: auth.currentUser.displayName || auth.currentUser.email,
        bbaSignerEmail: auth.currentUser.email,
        bbaSignature: bbaFormData,
        bbaSignedAt: new Date().toISOString(),
        bbaClientInfo: clientInfo,
        bbaESignCompliance: {
          intentToSign: true,
          consentToElectronicSignatures: true,
          consentToElectronicRecords: true,
          signatureAttribution: {
            fullName: auth.currentUser.displayName || auth.currentUser.email,
            email: auth.currentUser.email,
            ipAddress: clientInfo.ipAddress,
            timestamp: clientInfo.timestamp
          },
          auditTrail: {
            ipAddress: clientInfo.ipAddress,
            userAgent: clientInfo.userAgent,
            browser: clientInfo.browser,
            device: clientInfo.device,
            timezone: clientInfo.timezone,
            signedDateTime: clientInfo.timestamp
          }
        }
      };

      // Create audit trail record for BBA signature
      await this.createBBAAuditTrailRecord(userId, documentId, bbaSignature, clientInfo);

      return bbaSignature;
    } catch (error) {
      console.error('Error saving BBA signature:', error);
      throw error;
    }
  }

  // Create separate audit trail record for BBA signature
  static async createBBAAuditTrailRecord(userId, documentId, bbaSignature, clientInfo) {
    try {
      const auditTrailRef = collection(db, 'auditTrails');
      
      const auditRecord = {
        userId: userId,
        documentId: documentId,
        documentType: 'Non-Disclosure Agreement - BBA Signature',
        action: 'bba_document_signed',
        timestamp: serverTimestamp(),
        clientInfo: clientInfo,
        signatureData: {
          signerName: bbaSignature.bbaSignerName,
          signerEmail: bbaSignature.bbaSignerEmail,
          signature: bbaSignature.bbaSignature
        },
        eSignCompliance: bbaSignature.bbaESignCompliance
      };

      await addDoc(auditTrailRef, auditRecord);
    } catch (error) {
      console.error('Error creating BBA audit trail:', error);
      // Don't throw error here as the main signature should still be saved
    }
  }
}

export default ESignatureService; 